// Minimal translate-function shape — avoids coupling to a specific i18next
// TFunction overload signature (react-i18next's `t` satisfies this).
type TFn = (key: string, options?: Record<string, unknown>) => string;

// Forward status lifecycle. Index order is the single source of truth for
// "forward only" + single-step gating in the shipment form.
export const STATUS_ORDER = [
  "Order",
  "ReadyForPickup",
  "AwaitingPickup",
  "InTransit",
  "Completed",
] as const;

export type StatusKey = (typeof STATUS_ORDER)[number];

// Reasons mirror the backend `pohStatusOverrides.reason` enum. They explain
// which POH signature / driver-file step a manual status advance bypassed.
export type OverrideReason =
  | "pickup_files_skipped"
  | "delivery_files_skipped"
  | "pickup_driver_skipped"
  | "journey_start_manual"
  | "delivery_signatures_skipped"
  | "delivery_consignee_skipped";

type EdgeGate =
  | { type: "silent" }
  | { type: "block" }
  | { type: "confirm"; reason: OverrideReason };

// Decision for one forward single-step edge. `e` is the index of the lower
// status of the edge. MUST stay in sync with the backend `gateForEdge` in
// shipmentController.updateShipment.
function gateForEdge(level: number, isSeaAir: boolean, e: number): EdgeGate {
  if (e === 0) {
    // Order → ReadyForPickup
    if (level === 1 || level === 2) return { type: "block" };
    return { type: "silent" };
  }
  if (e === 1) {
    // ReadyForPickup → AwaitingPickup (Picked Up)
    if (level === 0) return { type: "confirm", reason: "pickup_files_skipped" };
    if (level === 1) return { type: "confirm", reason: "pickup_driver_skipped" };
    return isSeaAir
      ? { type: "silent" }
      : { type: "confirm", reason: "journey_start_manual" };
  }
  if (e === 2) {
    // AwaitingPickup → InTransit (start journey). This is a ROAD driver action;
    // SEA/AIR has no driver, so the admin moves it silently. Same for L1 and L2.
    if (level === 0) return { type: "silent" };
    return isSeaAir
      ? { type: "silent" }
      : { type: "confirm", reason: "journey_start_manual" };
  }
  if (e === 3) {
    // InTransit → Completed
    if (level === 0) return { type: "confirm", reason: "delivery_files_skipped" };
    if (level === 1)
      return { type: "confirm", reason: "delivery_signatures_skipped" };
    return { type: "confirm", reason: "delivery_consignee_skipped" };
  }
  return { type: "silent" };
}

export type StatusDecision =
  | { type: "silent" }
  | { type: "block"; message: string }
  | {
      type: "confirm";
      reason: OverrideReason;
      title: string;
      message: string;
      confirmText: string;
      cancelText: string;
    };

/**
 * Decide what should happen when an admin/user moves a shipment's status
 * forward from the shipment form. Returns whether to apply silently, prompt a
 * confirmation (and why), or block with a redirect-to-handover message.
 */
export function decideStatusChange(
  level: number,
  mode: string,
  fromStatus: string,
  toStatus: string,
  t: TFn,
): StatusDecision {
  const fromIdx = STATUS_ORDER.indexOf(fromStatus as StatusKey);
  const toIdx = STATUS_ORDER.indexOf(toStatus as StatusKey);

  // Not a forward move (backward is blocked by the dropdown + the backend).
  if (fromIdx === -1 || toIdx === -1 || toIdx <= fromIdx) return { type: "silent" };

  const isSeaAir = (mode || "ROAD") !== "ROAD";

  // The UI only offers single-step moves, but stay robust to a multi-step jump
  // by surfacing the first gate crossed (block wins, then confirm).
  for (let e = fromIdx; e < toIdx; e++) {
    const g = gateForEdge(level, isSeaAir, e);
    if (g.type === "block")
      return { type: "block", message: t("statusChange.shipperHandoverError") };
    if (g.type === "confirm") {
      return {
        type: "confirm",
        reason: g.reason,
        title: t("statusChange.confirmTitle"),
        message: t(`statusChange.reason.${g.reason}`),
        confirmText: t("statusChange.confirmText"),
        cancelText: t("statusChange.cancelText"),
      };
    }
  }
  return { type: "silent" };
}

/** Short human label for an override reason, for the POH views. */
export function overrideReasonLabel(reason: string, t: TFn): string {
  return t(`statusChange.reason.${reason}`);
}
